/**
 * The follow-up queue: every company somebody promised to come back to. The
 * follow-up tab of Kliendiotsing, the last step of the funnel.
 *
 * R6 gave the brain card a "next touch" box and an endpoint that lists what it
 * collected (`GET /brain/due`). What it did not give it was anywhere to LOOK at
 * that list, so the dates went into the database and stayed there - which is
 * the same fate the notes had before the box existed, one layer deeper. This
 * tab is the other half.
 *
 * ITS OWN VIEW, NOT A FILTER ON THE LEADS LIST. The two lists answer different
 * questions at different times. Vihjed answers "who is worth a call this
 * week", and it is worked by filtering a large pool down. This one is already
 * filtered - by a promise somebody made on the phone - and its whole content is
 * a handful of rows that must not be buried under a filter bar. That is why the
 * two are separate tabs of one page, each with its own toolbar, rather than one
 * list with a "promised" chip: routed tabs mount one body at a time and share
 * nothing but the page's head.
 *
 * THE BADGE COUNTS ONLY WHAT IS LATE, and the tab shows everything. A rail
 * counter that included next month's follow-ups would never reach zero, and a
 * number that never reaches zero is one people stop reading (the same lesson
 * `callsMissed` learned when it counted every no-answer since launch). The
 * badge sits on the sidebar's own follow-up row, which opens this tab, so a
 * red number lands on the promises it is counting.
 *
 * No head of its own: the page's head carries the title and this tab's
 * sentence, and the refresh sits at the end of the toolbar.
 */
import { useCallback, useEffect, useState } from "react";
import { CalendarCheck2, Loader2, RefreshCw } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform, type StaffUser } from "../../../lib/auth";
import { listBrainDue, setBrainPlan, type BrainDueRow } from "../../../lib/siteBrain";
import { Btn, EmptyState, ToggleChip, Toolbar, ToolbarGap, fmtNum, listCard, useToast } from "../../ui";
import { PopHost } from "../../shell/Pop";
import { BrainDialog } from "../ariregister/brain";
import { BrainDueRowItem } from "./BrainDueRowItem";
import { dayFromNow, groupDue } from "./lib";

/**
 * Company, when, last call, actions.
 *
 * TWO OF THE FOUR TRACKS ARE FIXED, AND BOTH FOR MEASURED REASONS.
 *
 * The actions track is 200px because four controls (register link, +7, +30,
 * clear) measure 192px plus the cell's own right padding. At the 148px it
 * started as, the group did not shrink and it did not scroll - a flex row with
 * `justify-end` simply overflowed 52px to the LEFT, and the register icon drew
 * on top of the last-call text one column over. `scrollWidth` never reported
 * it, because overflow on the start edge is invisible to it; the collision was
 * only findable by measuring where the first child actually landed.
 *
 * The date track is fixed because a date has ONE width. Sharing `1fr` with the
 * last-call column meant a narrow window clipped "05.08.2026" to "05...." -
 * spending the column's whole purpose to buy room for a secondary label that is
 * fine truncated.
 *
 * And the company track carries its own 220px floor rather than `minmax(0,...)`.
 * At the card's scroll floor the flexible tracks divide what is left, and with
 * no minimum of its own the company column fell to 181px - which renders
 * "sel-nadalal.ee" as "s.". A row that cannot say which company it is has
 * stopped being a row. `listFloor` reads this number and raises the width at
 * which the card starts scrolling, so nothing else has to be told about it.
 */
const COLS = "minmax(220px,2.4fr) 136px minmax(0,1fr) 200px";

export function BrainDueSection({ user }: { user: StaffUser }) {
  const t = useT();
  const toast = useToast();
  const canManage = userCanPlatform(user, "manage:ariregister");

  const [rows, setRows] = useState<BrainDueRow[] | null>(null);
  const [due, setDue] = useState(0);
  const [error, setError] = useState(false);
  /**
   * Late only, or everything.
   *
   * Defaults to everything. "Show me what is late" is one click away and the
   * opposite is not: an operator who lands on a filtered page with three rows
   * has no way of knowing the other thirty exist.
   */
  const [onlyDue, setOnlyDue] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [openFor, setOpenFor] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await listBrainDue({ limit: 200, onlyDue });
      setRows(res.rows);
      setDue(res.due);
      setError(false);
    } catch {
      setRows([]);
      setError(true);
    }
  }, [onlyDue]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Move a follow-up, or take it off the list.
   *
   * The row is dropped from local state on a clear rather than waiting for a
   * reload, because the reload is what makes the click feel slow and this is
   * the action people repeat. A postpone reloads: it can change which heading
   * the row belongs under, and moving it by hand here would mean a second copy
   * of the bucket rule.
   */
  async function replan(row: BrainDueRow, days: number | null) {
    if (busy) return;
    setBusy(row.domain);
    try {
      await setBrainPlan(row.domain, {
        nextTouchAt: days === null ? null : dayFromNow(days),
        note: days === null ? null : row.note,
      });
      if (days === null) {
        setRows((prev) => (prev ?? []).filter((r) => r.domain !== row.domain));
        setDue((n) => (row.due ? Math.max(0, n - 1) : n));
        toast(t("brain.plan.cleared"));
      } else {
        toast(t("brain.plan.saved"));
        await load();
      }
    } catch {
      toast(t("brain.plan.failed"));
    } finally {
      setBusy(null);
    }
  }

  const groups = groupDue(rows ?? []);

  return (
    <PopHost>
      <Toolbar>
        <span className="crm-sub px-1.5">
          {rows === null
            ? t("common.loading")
            : t("brainDue.count", { n: rows.length, due: fmtNum(due) })}
        </span>
        <ToolbarGap />
        <ToggleChip active={onlyDue} onClick={() => setOnlyDue((v) => !v)} count={due}>
          {t("brainDue.onlyLate")}
        </ToggleChip>
        <Btn small onClick={() => void load()} disabled={rows === null}>
          <RefreshCw className={rows === null ? "size-4 animate-spin" : "size-4"} />
          {t("common.refresh")}
        </Btn>
      </Toolbar>

      <div className="crm-card" style={listCard(COLS)}>
        {rows === null ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={CalendarCheck2}
            title={t(error ? "brainDue.errorTitle" : onlyDue ? "brainDue.emptyLate" : "brainDue.empty")}
            body={t(error ? "brainDue.errorBody" : onlyDue ? "brainDue.emptyLateBody" : "brainDue.emptyBody")}
          />
        ) : (
          <>
            <div className="crm-list-head" style={{ gridTemplateColumns: COLS, paddingTop: 22 }}>
              <span>{t("brainDue.col.company")}</span>
              <span>{t("brainDue.col.when")}</span>
              <span>{t("brainDue.col.lastCall")}</span>
              <span />
            </div>

            {groups.map((group) => (
              <div key={group.bucket} className="crm-rows pt-0">
                {/* The heading carries its own count. Scrolling to find out how
                    many are late is the thing the buckets exist to stop. */}
                <div className="crm-sub flex items-center gap-2 px-3 pb-1 pt-3 text-[12px] font-semibold uppercase tracking-wide">
                  {t(`brainDue.bucket.${group.bucket}`)}
                  <span className="crm-tnum crm-mut font-normal">{fmtNum(group.rows.length)}</span>
                </div>

                {group.rows.map((row) => (
                  <BrainDueRowItem
                    key={row.domain}
                    row={row}
                    cols={COLS}
                    canManage={canManage}
                    busy={busy === row.domain}
                    onOpen={() => setOpenFor(row.domain)}
                    onPostpone={(days) => void replan(row, days)}
                    onClear={() => void replan(row, null)}
                    t={t}
                  />
                ))}
              </div>
            ))}
          </>
        )}
      </div>

      {openFor ? (
        <BrainDialog
          domain={openFor}
          canManage={canManage}
          me={user.id}
          t={t}
          onClose={() => {
            setOpenFor(null);
            // The card can change the very date this list is sorted by, so the
            // list has to re-read when it closes. Without this the row an
            // operator just moved sits under its old heading until a refresh.
            void load();
          }}
        />
      ) : null}
    </PopHost>
  );
}
